import { HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { ClinicConfigTypeId } from 'src/app/shared/enums/clinic-config';
import { FrequencyInterval } from 'src/app/shared/enums/frequency-intervals';
import { ApiService } from 'src/app/shared/services/api.service';

import {
  IAddScheduleStaffShift,
  ICopyPatientScheduleData,
  ICopyPatientScheduleResponse,
  IPatientScheduleLastUpdatedDetails,
  IPatientTreatmentEvent,
  IStateRatio,
  IUpdatedTreatment,
  IUpdateScheduleStaffShift,
} from '../models/schedule-patient.models';

@Injectable({
  providedIn: 'root',
})
export class PatientScheduleService {
  private api = inject(ApiService);

  private $scheduleUpdate = new Subject<void>();
  private $missedTreatmentsCount = new Subject<number>();

  public watchScheduleUpdates() {
    return this.$scheduleUpdate.asObservable();
  }
  public UpdateSchedule() {
    return this.$scheduleUpdate.next();
  }
  public watchMissedTreatmentsCount() {
    return this.$missedTreatmentsCount.asObservable();
  }
  public emitMissedTreatmentsCount(count: number) {
    this.$missedTreatmentsCount.next(count);
  }

  getPatientSchedules(
    clinicId: number,
    idCenter: number,
    clinicConfigIds: number[],
    idEventSchedules: number[],
    startDate?: string,
    endDate?: string,
  ): Observable<IPatientTreatmentEvent[]> {
    let queryParams = new HttpParams().append('clinicId', clinicId).append('idCenter', idCenter);
    if (clinicConfigIds?.length) queryParams = queryParams.append('clinicConfigIds', clinicConfigIds.join(','));
    if (idEventSchedules?.length) queryParams = queryParams.append('idEventSchedules', idEventSchedules.join(','));
    if (startDate?.length) queryParams = queryParams.append('startDate', startDate);
    if (endDate?.length) queryParams = queryParams.append('endDate', endDate);
    return this.api.makeRequest('get', `patient-schedule/schedules/treatments`, {
      queryParams,
      ignoreErrors: true,
    });
  }
  getStaffShifts(
    clinicId: number,
    idCenter: number,
    clinicConfigIds: number[],
    idEventSchedules: number[],
  ): Observable<IPatientTreatmentEvent[]> {
    let queryParams = new HttpParams().append('clinicId', clinicId).append('idCenter', idCenter);
    if (clinicConfigIds?.length) queryParams = queryParams.append('clinicConfigIds', clinicConfigIds.join(','));
    if (idEventSchedules?.length) queryParams = queryParams.append('idEventSchedules', idEventSchedules.join(','));
    return this.api.makeRequest('get', `patient-schedule/schedules/patient-staff`, {
      queryParams,
      ignoreErrors: true,
    });
  }
  // startDate and endDate should be formatted as 'YYYY-MM-DD'
  // scheduleSourceType should be 5 (master) or 8 (sandbox)
  getWizardHours(clinicId: number, startDate: string, endDate: string, days: string): Observable<any> {
    return this.api.makeRequest(
      'get',
      `optimizer/wizard/hours/${clinicId}?startDate=${startDate}&endDate=${endDate}&days=${days}`,
      {
        ignoreErrors: true,
      },
    );
  }

  startWizard(
    idCenter: number,
    idEventSchedule: number,
    scheduleSourceType: ClinicConfigTypeId,
    frequencyInterval: FrequencyInterval,
    rnRatio: number,
    userId: number,
  ): Observable<any> {
    return this.api.makeRequest('post', `optimizer/wizard/${idCenter}`, {
      displayToast: false,
      payload: {
        eventScheduleID: idEventSchedule,
        sourceSchedule: scheduleSourceType,
        frequencyInterval,
        rnRatio,
        userId,
      },
    });
  }

  cancelWizard(clinicId: number, userId: string): Observable<any> {
    return this.api.makeRequest('post', `optimizer/cancel-wizard/${clinicId}`, {
      payload: {
        userId,
      },
    });
  }

  getRatios(idCenter: number): Observable<IStateRatio[]> {
    return this.api.makeRequest('get', `patient-schedule/ratios/${idCenter}`);
  }

  public addStaffScheduleShift(scheduleStaffShift: IAddScheduleStaffShift): Observable<void> {
    return this.api.makeRequest<void>('post', `patient-schedule/schedules/add-staff-shift`, {
      payload: {
        ...scheduleStaffShift,
      },
    });
  }

  public updateStaffScheduleShift(scheduleStaffShift: IUpdateScheduleStaffShift): Observable<void> {
    return this.api.makeRequest<void>('put', `patient-schedule/schedules/update-staff-shift`, {
      payload: {
        ...scheduleStaffShift,
      },
    });
  }

  public refreshScheduleFromMaster(
    source: ClinicConfigTypeId,
    idEventSchedule: number,
    clinicConfigId: number,
  ): Observable<ICopyPatientScheduleResponse> {
    const url =
      source === ClinicConfigTypeId.Whiteboard
        ? 'patient-schedule/schedules/refresh-whiteboard-from-master'
        : 'patient-schedule/schedules/refresh-sandbox-from-master';
    const payload = source === ClinicConfigTypeId.Whiteboard ? { clinicConfigId } : { idEventSchedule };
    return this.api.makeRequest<ICopyPatientScheduleResponse>('post', url, {
      payload,
    });
  }

  public copySandboxToMaster(idEventSchedule: number): Observable<ICopyPatientScheduleResponse> {
    return this.api.makeRequest<ICopyPatientScheduleResponse>(
      'post',
      `patient-schedule/schedules/copy-sandbox-to-master`,
      {
        payload: {
          idEventSchedule,
        },
      },
    );
  }

  public copyWizardToMasterOrSandbox(copyPatientScheduleData: ICopyPatientScheduleData): Observable<number> {
    return this.api.makeRequest<number>('post', `patient-schedule/schedules/copy-wizard-to-master-or-sandbox`, {
      payload: {
        idEventSchedule: copyPatientScheduleData.idEventSchedule,
        wizardEventScheduleId: copyPatientScheduleData.wizardEventScheduleId,
        wizardClinicConfigId: copyPatientScheduleData.wizardClinicConfigId,
      },
    });
  }

  /**
   * Function to create a new patient treatment event
   * @param idWeekDay - optional. If this is omitted, the treatment will be created for all 3 days of the schedule
   * If this param is included, the treatment will only be created for a specific day (ie. this will create a shared chair)
   */
  public createTreatmentEvent(
    idCenter: number,
    idEventSchedule: number,
    idChair: number,
    scheduleShift: number,
    startTime: string,
    durationInMinutes: number,
    idPatient: number,
    acuityOnPre_Minutes: number,
    acuityOffPre_Minutes: number,
    acuityOnPost_Minutes: number,
    acuityOffPost_Minutes: number,
    idWeekDay?: number,
  ): Observable<any> {
    return this.api.makeRequest<void>('post', `patient-schedule/chair/create-treatment`, {
      payload: {
        idCenter,
        idEventSchedule,
        idChair,
        scheduleShift,
        startTime,
        durationInMinutes,
        idPatient,
        acuityOnPre_Minutes,
        acuityOffPre_Minutes,
        acuityOnPost_Minutes,
        acuityOffPost_Minutes,
        idWeekDay,
      },
    });
  }

  public removePatient(
    patientId: number,
    scheduleId: number,
    chairId: number,
    clinicConfigId: number,
    idWeekDay: number,
  ): Observable<IUpdatedTreatment[]> {
    return this.api.makeRequest<IUpdatedTreatment[]>('post', `patient-schedule/chair/remove-patient`, {
      payload: {
        patientId,
        scheduleId,
        chairId,
        clinicConfigId,
        idWeekDay,
      },
    });
  }

  public addExistingPatient(patientData: {
    patientId: number;
    scheduleId: number;
    clinicConfigId: number;
    clinicConfigTypeId: number;
    chairId: number;
    frequencyInterval: FrequencyInterval;
    duration: number;
    acuityOnPreMinutes: number;
    acuityOffPreMinutes: number;
    acuityOnPostMinutes: number;
    acuityOffPostMinutes: number;
  }) {
    // modal shows the error
    return this.api.makeRequest<void>('post', `patient-schedule/chair/add-existing-patient`, {
      displayToast: false,
      payload: {
        patientId: patientData.patientId,
        scheduleId: patientData.scheduleId,
        clinicConfigId: patientData.clinicConfigId,
        chairId: patientData.chairId,
        duration: patientData.duration || 240,
        acuityOnPre: patientData.acuityOnPreMinutes,
        acuityOffPre: patientData.acuityOffPreMinutes,
        acuityOnPost: patientData.acuityOnPostMinutes,
        acuityOffPost: patientData.acuityOffPostMinutes,
        frequencyInterval: patientData.frequencyInterval,
        clinicConfigTypeId: patientData.clinicConfigTypeId,
        memo: '',
      },
    });
  }

  public insertPatient(patientData: {
    patientId: number;
    scheduleId?: number;
    chairId: number;
    startTime?: string;
    startDate?: string;
    endDate?: string;
    duration: number;
    acuityOnPre: number;
    acuityOffPre: number;
    acuityOnPost: number;
    acuityOffPost: number;
    memo: string;
    idWeekDay?: number;
    frequencyInterval?: number;
  }): Observable<IUpdatedTreatment[]> {
    return this.api.makeRequest<IUpdatedTreatment[]>('post', `patient-schedule/chair/insert-patient`, {
      payload: patientData,
    });
  }

  public createOpenChair({
    chairId,
    clinicConfigId,
    idWeekDay,
    startTime,
    note,
    duration,
  }: {
    chairId: number;
    clinicConfigId?: number;
    idWeekDay?: number;
    startTime: string;
    note?: string;
    duration?: number;
  }) {
    return this.api.makeRequest<void>('post', `patient-schedule/chair/make-chair-available`, {
      payload: {
        chairId,
        clinicConfigId,
        startTime,
        memo: note,
        idWeekDay,
        ...(duration && { duration }),
      },
    });
  }

  public makeChairUnavailable({
    scheduleId,
    clinicConfigId,
    chairId,
    idweekDay,
    status,
    reason,
    note,
    patientName,
    startDate,
    startTime,
  }: {
    scheduleId?: number;
    clinicConfigId: number;
    chairId: number;
    idweekDay?: number;
    status: string;
    reason: string;
    note?: string;
    patientName?: string;
    startDate?: string;
    startTime?: string;
  }) {
    return this.api.makeRequest<void>('POST', `patient-schedule/chair/make-chair-unavailable`, {
      payload: {
        patientId: 0,
        scheduleId,
        clinicConfigId,
        chairId,
        idweekDay,
        status,
        reason,
        note,
        patientName,
        startDate,
        startTime,
      },
    });
  }
  public swapPatients(
    patientId1: number,
    scheduleId1: number,
    chairId1: number,
    patientId2: number,
    scheduleId2: number,
    chairId2: number,
    clinicConfigId: number,
  ): Observable<IUpdatedTreatment[]> {
    return this.api.makeRequest<IUpdatedTreatment[]>('put', `patients/swap`, {
      payload: {
        patientId1,
        scheduleId1,
        chairId1,
        patientId2,
        scheduleId2,
        chairId2,
        clinicConfigId,
      },
    });
  }

  public getLastUpdatedDetails(
    centerId: number,
    scheduleTypeId: number,
  ): Observable<IPatientScheduleLastUpdatedDetails> {
    return this.api.makeRequest<IPatientScheduleLastUpdatedDetails>('post', `patient-schedule/last-updated-details`, {
      payload: {
        centerId,
        scheduleTypeId,
      },
    });
  }

  public getMissedTreatments(idCenter: number): Observable<{ patientIds: number[] }> {
    return this.api.makeRequest<{ patientIds: number[] }>('get', `patient-schedule/missed-treatments/${idCenter}`, {
      ignoreErrors: true,
      showIndicator: false,
    });
  }
}
